import KanbanIcon from "@atlaskit/icon-lab/core/kanban";
import TableIcon from "@atlaskit/icon/core/table";
import {token} from "@atlaskit/tokens";
import type {ReactNode} from "react";
import {useMemo} from "react";
import {twJoin} from "tailwind-merge";
import {useIsMobile} from "~/utilities/ottokart/mobileQuotationView";
import SmartLinkListIcon from "@atlaskit/icon/core/smart-link-list";

export type CollectionsListViewMode = "table" | "kanban" | "list";

const MOBILE_BREAKPOINT_PX = 768;

const COLLECTIONS_LIST_VIEW_MODE_KEY = "collections-list-view-mode:v1";

const VALID_VIEW_MODES = new Set<CollectionsListViewMode>(["table", "kanban", "list"]);

export function parseCollectionsListViewMode(value: unknown): CollectionsListViewMode | undefined {
    if (typeof value === "string" && VALID_VIEW_MODES.has(value as CollectionsListViewMode)) {
        return value as CollectionsListViewMode;
    }
    return undefined;
}

export function rememberCollectionsListViewMode(
    mode: CollectionsListViewMode,
    storageKey: string = COLLECTIONS_LIST_VIEW_MODE_KEY,
): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.setItem(storageKey, mode);
}

export function loadCollectionsListViewMode(storageKey: string = COLLECTIONS_LIST_VIEW_MODE_KEY): CollectionsListViewMode | null {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    const raw = sessionStorage.getItem(storageKey);
    if (raw === null) {
        return null;
    }
    return parseCollectionsListViewMode(raw) ?? null;
}

const DESKTOP_VIEW_ORDER = ["table", "kanban", "list"] satisfies Array<CollectionsListViewMode>;
const MOBILE_VIEW_ORDER = ["list", "kanban", "table"] satisfies Array<CollectionsListViewMode>;

const VIEW_MODE_CONFIG: Record<
    (typeof DESKTOP_VIEW_ORDER)[number],
    {
        label: string;
        icon: ReactNode;
    }
> = {
    table: {label: "Table", icon: <TableIcon label="" size="medium" />},
    kanban: {label: "Kanban", icon: <KanbanIcon label="" size="medium" />},
    list: {label: "List", icon: <SmartLinkListIcon label="" size="medium" />},
};

export function getDefaultCollectionsListViewMode(): CollectionsListViewMode {
    if (typeof window === "undefined") {
        return "table";
    }
    return window.innerWidth < MOBILE_BREAKPOINT_PX ? "list" : "table";
}

export function CollectionListViewSwitcher({
    value,
    onChange,
    className,
    storageKey = COLLECTIONS_LIST_VIEW_MODE_KEY,
    ariaLabel = "Product Collections view",
}: {
    value: CollectionsListViewMode;
    onChange: (mode: CollectionsListViewMode) => void;
    className?: string;
    storageKey?: string;
    ariaLabel?: string;
}) {
    const isMobile = useIsMobile(MOBILE_BREAKPOINT_PX);

    const orderedModes = useMemo(() => {
        const order = isMobile === true ? MOBILE_VIEW_ORDER : DESKTOP_VIEW_ORDER;
        return order.map((id) => ({id, ...VIEW_MODE_CONFIG[id]}));
    }, [isMobile]);

    return (
        <div
            className={twJoin("w-full border-b border-solid border-ds-border", className)}
            role="tablist"
            aria-label={ariaLabel}
        >
            <div className="grid w-max max-w-full grid-flow-col auto-cols-max items-end justify-start gap-x-ds-space-50">
                {orderedModes.map((mode) => {
                    const isSelected = value === mode.id;
                    return (
                        <button
                            key={mode.id}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            aria-label={`${mode.label} view`}
                            onClick={() => onChange(mode.id)}
                            className={twJoin(
                                "grid grid-flow-col auto-cols-max items-center gap-x-ds-space-050 p-ds-space-100",
                                "-mb-px border-b-[3px] border-solid bg-transparent cursor-pointer",
                                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-border-accent-blue",
                                isSelected ? "text-ds-text-accent-blue" : "border-transparent text-ds-text hover:text-ds-text",
                            )}
                            style={{
                                borderBottomColor: isSelected ? token("color.border.accent.blue") : "transparent",
                            }}
                        >
                            <span
                                className="grid place-items-center shrink-0 text-current [&_svg]:text-current"
                                aria-hidden
                            >
                                {mode.icon}
                            </span>
                            <span className="text-sm font-semibold leading-none tracking-tight whitespace-nowrap">{mode.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
